import { useEffect } from 'react';

interface UseClickOutsideHandlerProps {
  isOpen: boolean;
  sidebarRef: React.RefObject<HTMLDivElement>;
  handleClose: () => void;
  ignoreRefs?: React.RefObject<HTMLDivElement>[];
}

/**
 * Hook reutilizable para manejar clicks fuera de una sidebar
 * Ignora clicks en portales, toasts y otros elementos que no deben cerrar la sidebar
 */
export const useClickOutsideHandler = ({
  isOpen,
  sidebarRef,
  handleClose,
  ignoreRefs = [],
}: UseClickOutsideHandlerProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        const el = event.target as HTMLElement | null;
        if (!el || !el.isConnected) return; // Ignore if element is no longer in the DOM

        // Verificar que el elemento siga siendo parte del DOM
        if (el && !document.body.contains(el)) return;

        // Check if we are clicking on ignored refs (e.g., floating sidebars like AddressPickerSidebar)
        for (const ignoreRef of ignoreRefs) {
          if (ignoreRef.current && ignoreRef.current.contains(el)) return;
        }

        // Check if we are clicking on a portal (like Select, Popovers, Dialogs, Toasts, etc.)
        const isPortal =
          el.closest('[data-radix-portal]') ||
          el.closest('[data-radix-popper-content-wrapper]') ||
          el.closest('[role="dialog"]') ||
          el.closest('[role="listbox"]') ||
          el.closest('[role="menu"]') ||
          el.closest('[role="option"]') ||
          el.closest('[role="status"]') ||
          el.closest('[role="alert"]') ||
          el.closest('.leaflet-container') ||
          el.closest('.pac-container') ||
          el.closest('[toast-close]') ||
          el.closest('[data-radix-toast-announcer]') ||
          el.closest('[aria-label*="Toast"]') ||
          el.closest('[class*="toast"]') ||
          el.closest('[data-toast]');

        if (isPortal) return;

        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);
};
